import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { get } from 'lodash'
import { useAuthentication } from '../../contexts/AuthenticationContext'
import { useCurrentTeam } from '../../contexts/CurrentTeamContext'
import createEventEmitter from './eventEmitter'
import createActionCableClient from './actionCableClient'
import { CONNECTION_STATE, TASK_EVENTS, DEPLOYMENT_EVENTS, NODE_EVENTS, SERVICE_STATUS_EVENTS } from './constants'

const WebSocketContext = createContext(null)

export function WebSocketProvider({ children }) {
  const { isAuthenticated, getValidToken, registerLogoutCallback } = useAuthentication()
  const { currentTeamId } = useCurrentTeam()

  const [connectionState, setConnectionState] = useState(CONNECTION_STATE.DISCONNECTED)
  const [subscribedTeamId, setSubscribedTeamId] = useState(null)

  const eventEmitterRef = useRef(null)
  const clientRef = useRef(null)
  const currentSubscriptionRef = useRef(null)
  const mountedRef = useRef(true)

  // Initialize event emitter once
  if (!eventEmitterRef.current) {
    eventEmitterRef.current = createEventEmitter()
  }

  const emitter = eventEmitterRef.current

  // Map backend event types to our event types
  const mapEventType = useCallback((message) => {
    const action = get(message, 'action')

    // Check if this is a deployment event
    if (get(message, 'deployment')) {
      if (action === 'created') return DEPLOYMENT_EVENTS.CREATED
      return DEPLOYMENT_EVENTS.UPDATED
    }

    // Check if this is a node event
    if (get(message, 'node')) {
      if (action === 'created') return NODE_EVENTS.CREATED
      return NODE_EVENTS.UPDATED
    }

    // Check if this is a service status event
    if (get(message, 'service')) {
      if (action === 'created') return SERVICE_STATUS_EVENTS.CREATED
      return SERVICE_STATUS_EVENTS.UPDATED
    }

    // Task events
    const status = get(message, 'task.status') || get(message, 'status')

    if (action === 'created') return TASK_EVENTS.CREATED
    if (action === 'updated') {
      switch (status) {
        case 'completed':
          return TASK_EVENTS.COMPLETED
        case 'failed':
          return TASK_EVENTS.FAILED
        case 'pending':
          return TASK_EVENTS.PENDING
        case 'running':
          return TASK_EVENTS.RUNNING
        default:
          return TASK_EVENTS.UPDATED
      }
    }

    return TASK_EVENTS.UPDATED
  }, [])

  const handleMessage = useCallback((identifier, message) => {
    const eventType = mapEventType(message)

    // Extract the right data based on event type
    const isDeploymentEvent = eventType.startsWith('deployment.')
    const isNodeEvent = eventType.startsWith('node.')
    const isServiceStatusEvent = eventType.startsWith('service_status.')

    let data
    if (isDeploymentEvent) {
      data = get(message, 'deployment')
    } else if (isNodeEvent) {
      data = get(message, 'node')
    } else if (isServiceStatusEvent) {
      data = get(message, 'service')
    } else {
      data = get(message, 'task') || message
    }

    // Emit the specific event type
    emitter.emit(eventType, data)

    // Also emit generic updated event for all task status changes
    if (!isDeploymentEvent && !isNodeEvent && !isServiceStatusEvent && eventType !== TASK_EVENTS.CREATED && eventType !== TASK_EVENTS.UPDATED) {
      emitter.emit(TASK_EVENTS.UPDATED, data)
    }
  }, [emitter, mapEventType])

  const handleConnect = useCallback(() => {
    setConnectionState(CONNECTION_STATE.CONNECTED)
  }, [])

  const handleDisconnect = useCallback(() => {
    setConnectionState(CONNECTION_STATE.DISCONNECTED)
  }, [])

  const handleError = useCallback((error) => {
    console.error('WebSocket error:', error)
    setConnectionState(CONNECTION_STATE.DISCONNECTED)
  }, [])

  const handleSubscriptionConfirmed = useCallback((identifier) => {
    console.log('WebSocket subscription confirmed:', identifier)
  }, [])

  const handleSubscriptionRejected = useCallback((identifier) => {
    console.warn('WebSocket subscription rejected:', identifier)
  }, [])

  // Store refs for callbacks to avoid stale closures
  const getValidTokenRef = useRef(getValidToken)
  const handleMessageRef = useRef(handleMessage)
  const handleConnectRef = useRef(handleConnect)
  const handleDisconnectRef = useRef(handleDisconnect)
  const handleErrorRef = useRef(handleError)
  const handleSubscriptionConfirmedRef = useRef(handleSubscriptionConfirmed)
  const handleSubscriptionRejectedRef = useRef(handleSubscriptionRejected)

  // Keep refs updated
  getValidTokenRef.current = getValidToken
  handleMessageRef.current = handleMessage
  handleConnectRef.current = handleConnect
  handleDisconnectRef.current = handleDisconnect
  handleErrorRef.current = handleError
  handleSubscriptionConfirmedRef.current = handleSubscriptionConfirmed
  handleSubscriptionRejectedRef.current = handleSubscriptionRejected

  // Initialize client once on mount
  useEffect(() => {
    mountedRef.current = true

    clientRef.current = createActionCableClient({
      getToken: () => getValidTokenRef.current(),
      onMessage: (identifier, message) => {
        if (mountedRef.current) {
          handleMessageRef.current(identifier, message)
        }
      },
      onConnect: () => {
        if (mountedRef.current) {
          handleConnectRef.current()
        }
      },
      onDisconnect: (event) => {
        if (mountedRef.current) {
          handleDisconnectRef.current(event)
        }
      },
      onError: (error) => {
        if (mountedRef.current) {
          handleErrorRef.current(error)
        }
      },
      onSubscriptionConfirmed: (identifier) => {
        if (mountedRef.current) {
          handleSubscriptionConfirmedRef.current(identifier)
        }
      },
      onSubscriptionRejected: (identifier) => {
        if (mountedRef.current) {
          handleSubscriptionRejectedRef.current(identifier)
        }
      },
    })

    return () => {
      mountedRef.current = false
      if (clientRef.current) {
        clientRef.current.disconnect()
        clientRef.current = null
      }
      currentSubscriptionRef.current = null
    }
  }, [])

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && clientRef.current) {
      setConnectionState(CONNECTION_STATE.CONNECTING)

      clientRef.current.connect()
        .catch((error) => {
          console.error('WebSocket connection failed:', error)
          if (mountedRef.current) {
            setConnectionState(CONNECTION_STATE.DISCONNECTED)
          }
        })
    } else if (!isAuthenticated && clientRef.current) {
      clientRef.current.disconnect()
      currentSubscriptionRef.current = null
      setSubscribedTeamId(null)
    }
  }, [isAuthenticated])

  // Register logout callback to disconnect
  useEffect(() => {
    const unregister = registerLogoutCallback(() => {
      if (clientRef.current) {
        clientRef.current.disconnect()
        currentSubscriptionRef.current = null
        setSubscribedTeamId(null)
        emitter.clear()
      }
    })

    return () => {
      if (unregister) {
        unregister()
      }
    }
  }, [registerLogoutCallback, emitter])

  // Subscribe to current team channel only
  useEffect(() => {
    if (connectionState !== CONNECTION_STATE.CONNECTED || !clientRef.current) {
      return
    }

    const client = clientRef.current

    // Unsubscribe from previous team if different
    if (currentSubscriptionRef.current && subscribedTeamId !== currentTeamId) {
      client.unsubscribe(currentSubscriptionRef.current)
      currentSubscriptionRef.current = null
      setSubscribedTeamId(null)
    }

    // Subscribe to new team if we have one
    if (currentTeamId && !currentSubscriptionRef.current) {
      const identifier = client.subscribe('TeamChannel', { team_id: currentTeamId })
      currentSubscriptionRef.current = identifier
      setSubscribedTeamId(currentTeamId)
    }
  }, [connectionState, currentTeamId, subscribedTeamId])

  // Public API
  const subscribe = useCallback((eventType, callback) => {
    return emitter.on(eventType, callback)
  }, [emitter])

  const value = useMemo(() => ({
    connectionState,
    subscribedTeamId,
    subscribe,
    isConnected: connectionState === CONNECTION_STATE.CONNECTED,
  }), [connectionState, subscribedTeamId, subscribe])

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
